import {
  useCheapestAvailability,
  useCompetitorChurn,
  useCompetitors,
  useDeliveredRank,
  usePromotedSlot,
  useShopRatings,
} from '../api/queries';
import { AvailabilitySplitChart } from '../charts/AvailabilitySplitChart';
import { CompetitorChart } from '../charts/CompetitorChart';
import { RankShiftChart } from '../charts/RankShiftChart';
import { RatingPriceScatter } from '../charts/RatingPriceScatter';
import { DIVERGE, SERIES, STATUS } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Delta } from '../components/Delta';
import { Empty, Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, money, percent, plainPercent, shortDate } from '../utils/format';

/** A rival unseen for this long has probably dropped the line, not just repriced. */
const GONE_AFTER_DAYS = 21;
const NEW_WITHIN_DAYS = 60;

export function CompetitorsPage() {
  const competitors = useCompetitors(30);
  const delivered = useDeliveredRank(30);
  const ratings = useShopRatings(30);
  const availability = useCheapestAvailability();
  const promoted = usePromotedSlot();
  const churn = useCompetitorChurn(180, 30);

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Competitors</h1>
          <p className="page__lede">
            Shops you meet on Heureka listings, ranked by how often they undercut you. Figures use the newest scan of
            each shared product, so no stale price is counted twice.
          </p>
        </div>
      </header>

      {competitors.isError ? (
        <Failed error={competitors.error} />
      ) : competitors.isLoading ? (
        <Loading label="Loading competitors…" />
      ) : (competitors.data?.length ?? 0) === 0 ? (
        <Empty>
          <div>No competitor offers captured yet.</div>
          <div className="muted">They appear as the crawler works through the catalogue.</div>
        </Empty>
      ) : (
        <>
          <Card title="Who undercuts you, and how often" note="Shared products split by who holds the lower price">
            <CompetitorChart stats={competitors.data ?? []} />
          </Card>

          <Card
            title="Does shipping flip your ranking?"
            note="Rank on the product price against rank once each offer's shipping is added. Heureka sorts on the product price; the buyer pays the delivered one."
          >
            {delivered.isLoading ? (
              <Loading />
            ) : delivered.isError ? (
              <Failed error={delivered.error} />
            ) : (
              <RankShiftChart rows={delivered.data ?? []} />
            )}
          </Card>

          <div className="grid grid--halves">
            <Card
              title="Rating against price level"
              note="Bubble size is how many offers we have seen. The dashed line at 100% is the market low; your shop is highlighted."
            >
              {ratings.isLoading ? (
                <Loading />
              ) : ratings.isError ? (
                <Failed error={ratings.error} />
              ) : (
                <RatingPriceScatter rows={ratings.data ?? []} />
              )}
            </Card>

            <Card
              title="Is the cheapest offer actually in stock?"
              note="Availability claimed by the cheapest competing offer, in Heureka's own wording"
            >
              {availability.isLoading ? (
                <Loading />
              ) : availability.isError ? (
                <Failed error={availability.error} />
              ) : (
                <AvailabilitySplitChart rows={availability.data ?? []} />
              )}
            </Card>
          </div>

          <Card
            title="The promoted slot"
            note="Heureka lifts one offer above the list; whether it holds the cheapest price decides what being cheapest is worth"
          >
            {promoted.isLoading ? (
              <Loading />
            ) : promoted.isError ? (
              <Failed error={promoted.error} />
            ) : (
              <div className="grid grid--tiles">
                <StatTile
                  label="Listings with a promoted offer"
                  value={count(promoted.data?.scansWithPromoted)}
                  foot="of the newest scan of each product"
                  accent={SERIES[0]}
                />
                <StatTile
                  label="Promoted offer is the cheapest"
                  value={count(promoted.data?.promotedIsCheapest)}
                  foot={
                    promoted.data && promoted.data.scansWithPromoted > 0
                      ? `${percent(
                          (promoted.data.promotedIsCheapest / promoted.data.scansWithPromoted) * 100,
                        )} of them`
                      : '—'
                  }
                  accent={STATUS.good}
                />
                <StatTile
                  label="Premium the promoted offer carries"
                  value={
                    promoted.data?.avgPromotedPremiumPct === null ||
                    promoted.data?.avgPromotedPremiumPct === undefined
                      ? '—'
                      : percent(promoted.data.avgPromotedPremiumPct)
                  }
                  foot="mean, above the cheapest offer on the listing"
                  accent={DIVERGE.high}
                />
                <StatTile
                  label="Times the slot was yours"
                  value={count(promoted.data?.timesWeWerePromoted)}
                  foot="promotion is paid placement, not a price reward"
                  accent={SERIES[6]}
                />
              </div>
            )}
          </Card>

          <Card
            title="Who arrived, who left"
            note="First and last time each shop appeared on your listings, over the last 180 days"
          >
            {churn.isLoading ? (
              <Loading />
            ) : churn.isError ? (
              <Failed error={churn.error} />
            ) : (churn.data?.length ?? 0) === 0 ? (
              <div className="state">No competitor history yet.</div>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th>Status</th>
                      <th className="num">Products</th>
                      <th className="num">Offers seen</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {churn.data?.map((shop) => {
                      const gone = shop.daysSinceLastSeen > GONE_AFTER_DAYS;
                      const fresh = shop.daysSinceFirstSeen <= NEW_WITHIN_DAYS;

                      return (
                        <tr key={shop.shopName}>
                          <td>{shop.shopName}</td>
                          <td>
                            {gone ? (
                              <Badge label={`Gone ${shop.daysSinceLastSeen}d`} color={DIVERGE.highSoft} />
                            ) : fresh ? (
                              <Badge label={`New ${shop.daysSinceFirstSeen}d`} color={SERIES[5]} />
                            ) : (
                              <Badge label="Active" color={STATUS.good} />
                            )}
                          </td>
                          <td className="num">{count(shop.products)}</td>
                          <td className="num secondary">{count(shop.offers)}</td>
                          <td className="secondary">{shortDate(shop.firstSeenAt)}</td>
                          <td className="secondary">{shortDate(shop.lastSeenAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="All competitors" note={`${count(competitors.data?.length)} shops`}>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th className="num">Shared products</th>
                    <th className="num">They undercut</th>
                    <th className="num">We hold</th>
                    <th className="num">Your win rate</th>
                    <th className="num">Mean diff</th>
                    <th className="num">Mean diff €</th>
                    <th className="num">Heureka rating</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.data?.map((competitor) => (
                    <tr key={competitor.shopName}>
                      <td>{competitor.shopName}</td>
                      <td className="num">{count(competitor.productCount)}</td>
                      <td className="num">{count(competitor.cheaperCount)}</td>
                      <td className="num">{count(competitor.pricierCount)}</td>
                      <td className="num">{plainPercent(competitor.winRate, 1)}</td>
                      <td className="num">
                        {/* their price minus ours: positive means they are dearer, which is good for us */}
                        <Delta value={competitor.avgDiffPct} lowerIsBetter={false} />
                      </td>
                      <td className="num secondary">{money(competitor.avgDiffEur)}</td>
                      <td className="num secondary">
                        {competitor.avgRatingPercent === null ? '—' : `${competitor.avgRatingPercent}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
